import { useState, lazy, Suspense } from "react";
import { Link } from "@/lib/router-compat";
import { Menu, X, ChevronDown, User } from "lucide-react";
import { useCountry } from "@/hooks/useCountry";
import { Button } from "@/components/ui/button";
const CartDrawer = lazy(() => import("@/components/cart/CartDrawer").then((m) => ({ default: m.CartDrawer })));
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

// Logo servi depuis /public pour URL stable (preload index.html)
const logoLapaire = "/logo-lapaire.webp";

export function Header() {
  const { country, setCountry, t, countries } = useCountry();
  const [mobileOpen, setMobileOpen] = useState(false);

  const navItems = [
    { label: t("nav.findStore"), href: `/${country.slug}/stores` },
    { label: t("nav.glasses"), href: `/${country.slug}/glasses` },
    { label: t("nav.prices"), href: `/${country.slug}/prices` },
    { label: "Carrières", href: `/careers` },
    { label: "À propos", href: `/about-us` },
  ];

  return (
    <header className="sticky top-0 z-50 bg-background/95 backdrop-blur border-b border-border">
      <div className="w-full flex items-center justify-between h-14 px-3 sm:px-4">
        <Link to={`/${country.slug}`}>
          <img src={logoLapaire} alt="Lapaire" className="h-14 w-auto" width={112} height={56} />
        </Link>

        <nav className="hidden lg:flex items-center gap-6">
          {navItems.map((item) => (
            <Link
              key={item.href}
              to={item.href}
              className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
            >
              {item.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="gap-1 text-sm">
                {country.flag} <span className="hidden sm:inline">{country.name}</span>
                <ChevronDown className="h-3 w-3" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {countries.map((c) => (
                <DropdownMenuItem key={c.slug} onClick={() => setCountry(c.slug)}>
                  {c.flag} {c.name}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <Suspense fallback={null}><CartDrawer /></Suspense>

          <Link to={`/${country.slug}/account`} className="hidden lg:inline-flex">
            <Button variant="ghost" size="icon" aria-label={t("nav.book") /* generic */}>
              <User className="h-5 w-5" />
            </Button>
          </Link>

          <Link
            to={`/${country.slug}/book`}
            className="hidden lg:block"
            onClick={() => {
              import("@/lib/analytics/track").then(({ track }) =>
                track("cta_click", {
                  cta_name: "book_free_eye_test",
                  cta_type: "primary",
                  cta_location: "header_desktop",
                  destination_url: `/${country.slug}/book`,
                })
              );
            }}
          >
            <Button size="sm" className="font-semibold">
              {t("nav.book")}
            </Button>
          </Link>

          <Button
            variant="ghost"
            size="icon"
            className="lg:hidden"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label={mobileOpen ? "Fermer le menu" : "Ouvrir le menu"}
          >
            {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </Button>
        </div>
      </div>

      {mobileOpen && (
        <nav className="lg:hidden border-t border-border bg-background px-4 pb-4 pt-2 space-y-2">
          {navItems.map((item) => (
            <Link
              key={item.href}
              to={item.href}
              onClick={() => setMobileOpen(false)}
              className="block py-2 text-sm font-medium text-foreground"
            >
              {item.label}
            </Link>
          ))}
          <Link
            to={`/${country.slug}/account`}
            onClick={() => setMobileOpen(false)}
            className="block py-2 text-sm font-medium text-muted-foreground"
          >
            Mon compte
          </Link>

          <Link
            to={`/${country.slug}/book`}
            className="block pt-3"
            onClick={() => {
              setMobileOpen(false);
              import("@/lib/analytics/track").then(({ track }) =>
                track("cta_click", {
                  cta_name: "book_free_eye_test",
                  cta_type: "primary",
                  cta_location: "header_mobile",
                  destination_url: `/${country.slug}/book`,
                })
              );
            }}
          >
            <Button size="lg" className="w-full font-semibold">
              {t("nav.book")}
            </Button>
          </Link>
        </nav>
      )}
    </header>
  );
}